import type {
  CreateTourPayload,
  ITourPoi,
  TourListParams,
  UpdateTourPayload,
} from '@/shared/api/types/tour-types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  addTourPois,
  createTour,
  deleteTour,
  getTourById,
  getTours,
  getToursSuperadmin,
  removeTourPoi,
  removeTourPois,
  saveTourPois,
  updateTour,
  updateTourPoiColor,
  updateTourPoisBatch,
  updateTourPoiOrder,
  uploadTourImage,
} from '@/shared/api/services/tour-service'
import { AdminScopeType } from '@/shared/constants/admin-scope'

type TourQueryScope = AdminScopeType.Tenant | AdminScopeType.Super

export const TOUR_QUERY_KEYS = {
  all: ['tours'] as const,
  listPrefix: () => [...TOUR_QUERY_KEYS.all, 'list'] as const,
  list: (scope: TourQueryScope, tenantId: string, params: TourListParams) =>
    [...TOUR_QUERY_KEYS.all, 'list', scope, tenantId, params] as const,
  detailPrefix: (id: string) => [...TOUR_QUERY_KEYS.all, 'detail', id] as const,
  detail: (id: string, languageCode?: string) =>
    [...TOUR_QUERY_KEYS.all, 'detail', id, languageCode] as const,
}

export function useToursQuery(
  tenantId: string,
  params: TourListParams,
  options?: { scope?: TourQueryScope },
) {
  const scope = options?.scope ?? AdminScopeType.Tenant
  return useQuery({
    queryKey: TOUR_QUERY_KEYS.list(scope, tenantId, params),
    queryFn: async () =>
      scope === AdminScopeType.Super ? getToursSuperadmin(params) : getTours(params),
    enabled: !!tenantId,
    staleTime: 0,
  })
}

export function useTourByIdQuery(id: string, languageCode?: string) {
  return useQuery({
    queryKey: TOUR_QUERY_KEYS.detail(id, languageCode),
    queryFn: async () => getTourById(id, languageCode),
    enabled: !!id,
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

export function useCreateTourMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: CreateTourPayload) => createTour(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: TOUR_QUERY_KEYS.all })
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useUpdateTourMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UpdateTourPayload }) =>
      updateTour(id, payload),
    onSuccess: async (_data, { id }) => {
      await queryClient.invalidateQueries({ queryKey: TOUR_QUERY_KEYS.listPrefix() })
      await queryClient.invalidateQueries({ queryKey: TOUR_QUERY_KEYS.detailPrefix(id) })
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useDeleteTourMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => deleteTour(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: TOUR_QUERY_KEYS.all })
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useUpdateTourPoiOrderMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, pois }: { id: string; pois: ITourPoi[] }) =>
      updateTourPoiOrder(id, pois),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: TOUR_QUERY_KEYS.detailPrefix(id) })
    },
  })
}

export function useAddTourPoisMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, poiIds }: { id: string; poiIds: string[] }) => addTourPois(id, poiIds),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: TOUR_QUERY_KEYS.detailPrefix(id) })
    },
  })
}

export function useRemoveTourPoiMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, poiId }: { id: string; poiId: string }) => removeTourPoi(id, poiId),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: TOUR_QUERY_KEYS.detailPrefix(id) })
    },
  })
}

export function useRemoveTourPoisMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, poiIds }: { id: string; poiIds: string[] }) =>
      removeTourPois(id, poiIds),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: TOUR_QUERY_KEYS.detailPrefix(id) })
    },
  })
}

export function useSaveTourPoisMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, poiIds }: { id: string; poiIds: string[] }) =>
      saveTourPois(id, poiIds),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: TOUR_QUERY_KEYS.detailPrefix(id) })
      void queryClient.invalidateQueries({ queryKey: TOUR_QUERY_KEYS.listPrefix() })
    },
  })
}

export function useUploadTourImageMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, imageFile }: { id: string; imageFile: File }) =>
      uploadTourImage(id, imageFile),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: TOUR_QUERY_KEYS.detailPrefix(id) })
      void queryClient.invalidateQueries({ queryKey: TOUR_QUERY_KEYS.listPrefix() })
    },
  })
}

export function useUpdateTourPoiColorMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, poiId, color }: { id: string; poiId: string; color: string | null }) =>
      updateTourPoiColor(id, poiId, color),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: TOUR_QUERY_KEYS.detailPrefix(id) })
    },
  })
}

export function useUpdateTourPoisBatchMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, pois }: { id: string; pois: ITourPoi[] }) =>
      updateTourPoisBatch(id, pois),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: TOUR_QUERY_KEYS.detailPrefix(id) })
    },
  })
}
